import { useEffect, useRef, useState } from "react";
import { CheckCircle, CreditCard, Lifebuoy, X, Spinner, Money, PencilSimple } from "@phosphor-icons/react";
import { kioskApi, errText, isServiceError } from "@/lib/kioskApi";

const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
// Connection trouble never means the payment failed — the server re-checks the terminal before any new attempt.
const cardErr = (e) => (isServiceError(e) ? "Connection hiccup — we're checking your payment with the terminal. Please don't pay twice." : errText(e));

export function KioskReview({ session, onConfirm, onChange, busy }) {
  return (
    <div className="fixed inset-0 z-30 bg-[#0f2a3d]/60 backdrop-blur-md flex items-center justify-center p-4" data-testid="kiosk-review">
      <div className="bg-white rounded-[28px] w-full max-w-xl p-7 kiosk-rise">
        <div className="text-xs uppercase tracking-[0.2em] text-[#5c7385]">Please check your order</div>
        <div className="mt-4 space-y-2 max-h-[40vh] overflow-y-auto">
          {session.cart.map((ln, i) => (
            <div key={i} className="flex justify-between text-lg text-[#0f2a3d]"><span>{ln.qty} × {ln.name}{ln.modifiers.length ? <span className="text-[#5c7385] text-base"> · {ln.modifiers.map((m) => m.name).join(", ")}</span> : null}</span><span className="tabular-nums">{fmt(ln.line_total)}</span></div>
          ))}
        </div>
        <div className="mt-5 pt-4 border-t text-[#5c7385] space-y-1">
          {session.credit?.apply_amount > 0 && <div className="flex justify-between"><span>Account credit</span><span>− {fmt(session.credit.apply_amount)}</span></div>}
          <div className="flex justify-between text-3xl font-bold font-heading text-[#0f2a3d]"><span>{session.payment === "cash" ? "Pay at counter" : session.payment === "card" ? "Pay by card" : "To pay"}</span><span data-testid="kiosk-review-due">{fmt(session.estimated_due)}</span></div>
        </div>
        <div className="text-sm text-[#5c7385] mt-2">Say “yes” or tap Confirm.</div>
        <div className="flex gap-3 mt-6">
          <button className="flex-1 h-16 rounded-full bg-[#eef2f5] text-[#0f2a3d] text-lg" data-testid="kiosk-review-change" onClick={onChange}>Change order</button>
          <button className="flex-[1.4] h-16 rounded-full bg-[#e2572b] text-white text-xl font-semibold disabled:opacity-50" disabled={busy} data-testid="kiosk-review-confirm" onClick={onConfirm}>{busy ? "Placing…" : "Confirm order"}</button>
        </div>
      </div>
    </div>
  );
}

// Card payment on the shared WisePOS E — queued server-side when another device is using it.
export function KioskCard({ session, onUpdate, simulated, cashOk }) {
  const card = session.card || {};
  const [err, setErr] = useState("");
  const live = ["queued", "processing", "idle", "checking"].includes(card.state);
  const tick = useRef(null);
  useEffect(() => {
    if (!live) return undefined;
    const run = async () => {
      try { const r = await kioskApi.post(`/sessions/${session.id}/card/poll`); setErr(""); onUpdate(r.data.session); } catch (e) { setErr(cardErr(e)); }
    };
    tick.current = setInterval(run, 2000);
    return () => clearInterval(tick.current);
  }, [live, session.id, onUpdate]);
  const op = async (o) => {
    setErr("");
    try { const r = await kioskApi.post(`/sessions/${session.id}/card/${o}`); onUpdate(r.data.session); } catch (e) { setErr(cardErr(e)); }
  };
  const failed = ["declined", "expired", "cancelled", "timeout", "error"].includes(card.state);
  return (
    <div className="fixed inset-0 z-30 bg-[#0f2a3d] flex items-center justify-center p-6 text-white" data-testid="kiosk-card">
      <div className="max-w-xl w-full text-center kiosk-rise">
        <div className={`mx-auto h-28 w-28 rounded-full flex items-center justify-center ${failed ? "bg-[#7a2e14]" : "bg-[#173f5a] kiosk-breathe"}`}>
          {card.state === "queued" || card.state === "checking" ? <Spinner size={52} className="animate-spin" /> : <CreditCard size={56} />}
        </div>
        <div className="text-5xl font-bold font-heading mt-8 tabular-nums" data-testid="kiosk-card-amount">{fmt(session.order?.amount_due)}</div>
        <div className="text-2xl mt-4" data-testid="kiosk-card-message">
          {card.state === "queued" ? "EFTPOS terminal busy, please wait." : card.state === "processing" ? "Tap, insert or swipe your card on the EFTPOS terminal."
            : card.state === "idle" ? (session.order?.total_changed ? `Your total is ${fmt(session.order.amount_due)} — tap Pay by card.` : "Getting the EFTPOS terminal ready…") : card.message || "Card payment didn't go through."}
        </div>
        {card.state === "queued" && <div className="text-[#9fb6c6] mt-2" data-testid="kiosk-card-queue">We'll start your payment automatically as soon as it's free.</div>}
        {err && <div className="text-[#ffb59a] mt-3" data-testid="kiosk-card-error">{err}</div>}
        {(failed || card.state === "checking") && <SavedOrder session={session} />}
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {(failed || (card.state === "idle" && session.order?.total_changed)) && <button className="kiosk-big bg-[#e2572b]" data-testid="kiosk-card-retry" onClick={() => op(card.pi_canceled ? "fresh" : "start")}><CreditCard size={22} /> {card.state === "idle" ? "Pay by card" : "Try Card again"}</button>}
          {failed && cashOk && <button className="kiosk-big bg-white !text-[#0f2a3d]" data-testid="kiosk-card-to-cash" onClick={() => op("cash")}><Money size={22} /> Pay Cash at Counter</button>}
          {failed && <button className="kiosk-big bg-white/10" data-testid="kiosk-card-edit" onClick={() => op("edit")}><PencilSimple size={22} /> Change order</button>}
          {(failed || card.state === "checking") && <button className="kiosk-big bg-white/10" data-testid="kiosk-card-help" onClick={() => op("help")}><Lifebuoy size={22} /> Staff assistance</button>}
          {["queued", "processing"].includes(card.state) && <button className="kiosk-big bg-white/10" data-testid="kiosk-card-cancel" onClick={() => op("cancel")}><X size={22} /> Cancel payment</button>}
        </div>
        {simulated && card.state === "processing" && <SimTap piId={card.pi_id} />}
        <div className="text-sm text-[#9fb6c6] mt-8">Nothing goes to the kitchen until your payment is approved or you choose Pay Cash at Counter.</div>
      </div>
    </div>
  );
}

function SavedOrder({ session }) {
  return (
    <div className="mt-6 mx-auto max-w-md text-left rounded-2xl bg-white/[0.06] border border-white/10 px-5 py-4" data-testid="kiosk-card-saved-order">
      {session.cart.map((ln, i) => (
        <div key={i} className="flex justify-between text-white/90"><span>{ln.qty} × {ln.name}{ln.modifiers.length ? ` · ${ln.modifiers.map((m) => m.name).join(", ")}` : ""}</span><span className="tabular-nums">{fmt(ln.line_total)}</span></div>
      ))}
    </div>
  );
}

function SimTap({ piId }) {
  return <div className="mt-6 text-xs text-[#9fb6c6]" data-testid="kiosk-card-simulated">PREVIEW: simulated EFTPOS terminal · payment {piId}</div>;
}

export function KioskDone({ session, onDone, seconds = 20 }) {
  const o = session.order || {};
  useEffect(() => { const t = setTimeout(onDone, seconds * 1000); return () => clearTimeout(t); }, [onDone, seconds]);
  const counter = o.payment_method === "pay_at_counter";
  if (o.test) return (
    <div className="fixed inset-0 z-30 bg-[#f4f1ea] flex items-center justify-center p-6" data-testid="kiosk-done">
      <div className="max-w-xl w-full text-center kiosk-rise">
        <CheckCircle size={84} weight="fill" className="mx-auto text-[#a15c00]" />
        <div className="text-[5rem] leading-none font-bold font-heading text-[#0f2a3d] mt-6" data-testid="kiosk-done-number">TEST</div>
        <div className="mt-6 text-xl text-[#0f2a3d]" data-testid="kiosk-done-test">Staff test complete — {fmt(o.total)} ({counter ? "Pay at Counter" : o.payment_method === "credit" ? "account credit" : "card"} chosen). No order was sent to the POS or kitchen, no payment was taken and no stock was used.</div>
        <button className="mt-10 h-16 px-10 rounded-full bg-[#0f2a3d] text-white text-xl" data-testid="kiosk-done-new" onClick={onDone}>Done</button>
      </div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-30 bg-[#f4f1ea] flex items-center justify-center p-6" data-testid="kiosk-done">
      <div className="max-w-xl w-full text-center kiosk-rise">
        <CheckCircle size={84} weight="fill" className="mx-auto text-[#1f7a4d]" />
        <div className="text-lg uppercase tracking-[0.25em] text-[#5c7385] mt-6">Order number</div>
        <div className="text-[7rem] leading-none font-bold font-heading text-[#0f2a3d]" data-testid="kiosk-done-number">{o.number ?? "—"}</div>
        {counter ? (
          <div className="mt-6 text-2xl text-[#0f2a3d]" data-testid="kiosk-done-owing">Please pay <b>{fmt(o.amount_due)}</b> at the counter when you collect your order.</div>
        ) : (
          <div className="mt-6 text-2xl text-[#0f2a3d]" data-testid="kiosk-done-paid">Paid — thank you!{o.credit_used ? ` (${fmt(o.credit_used)} account credit used)` : ""}</div>
        )}
        <button className="mt-10 h-16 px-10 rounded-full bg-[#0f2a3d] text-white text-xl" data-testid="kiosk-done-new" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}
